import { Formik, FormikConfig, FormikContext, FormikProps, FormikValues, useFormik } from 'formik';
import React from 'react';

const NestableFormsContext = React.createContext<{
  formsRef: React.MutableRefObject<Record<string, FormikProps<any>>>;
} | null>(null);


export function NestableFormik<T extends FormikValues = FormikValues>(props: FormikConfig<T>) {
  const formsRef = React.useRef<Record<string, FormikProps<any>>>({});

  const value = React.useMemo(() => ({
    formsRef: formsRef,
  }), []);

  return (
    <NestableFormsContext.Provider value={value}>
      <Formik
        {...props}
        onSubmit={async (values, helpers) => {
          const forms = Object.keys(formsRef.current).map(k => formsRef.current[k]);

          for (const form of forms) {
            const errors = await form.validateForm();

            if (Object.keys(errors).length) {
              form.setErrors(errors);
              form.setTouched(Object.keys(errors).reduce((prev, curr) => ({
                ...prev,
                [curr]: true
              }), {} as Record<string, boolean>));
              return;
            } else if (form.dirty) {
              await form.submitForm();
            }
          }

          return await props.onSubmit(values, helpers);
        }}
      />
    </NestableFormsContext.Provider>
  );
}


export function NestedFormik<T extends FormikValues = FormikValues>({ children, ...props }: FormikConfig<T> & { name?: string }) {
  const formik: FormikProps<T> = useFormik(props);
  const nestableFormik = React.useContext(NestableFormsContext);

  React.useEffect(() => {
    if (!nestableFormik || !props.name) {
      return;
    }

    nestableFormik.formsRef.current[props.name] = formik;

    return () => {
      if (nestableFormik && props.name) {
        delete nestableFormik.formsRef.current[props.name];
      }
    };
  }, [ props.name, formik, nestableFormik ]);

  const renderedChildren = React.useMemo(() => {
    if (typeof children === 'function') {
      return (
        children?.(formik)
      );
    }

    return children;
  }, [ children, formik ]);

  return (
    <FormikContext.Provider value={formik}>
      {renderedChildren}
    </FormikContext.Provider>
  );
}
